import React from 'react';
import {
  Button,
  Checkbox,
  DatePicker,
  Input,
  Radio,
  Select,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, CloseOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;

const renderComponent = (component) => {
  const key = component.id || component.name || component.type;

  switch (component.type) {
    case 'text_large_heading':
      return (
        <h3 key={key} style={{ fontWeight: 700, fontSize: 20, margin: '0 0 12px', color: '#111' }}>
          {component.text || 'Heading'}
        </h3>
      );
    case 'text_small_heading':
      return (
        <h4 key={key} style={{ fontWeight: 600, fontSize: 16, margin: '0 0 10px', color: '#222' }}>
          {component.text || 'Subheading'}
        </h4>
      );
    case 'text_body':
      return (
        <p key={key} style={{ fontSize: 15, lineHeight: 1.5, margin: '0 0 12px', color: '#333' }}>
          {component.text || ''}
        </p>
      );
    case 'text_caption':
      return (
        <p key={key} style={{ fontSize: 13, lineHeight: 1.4, margin: '0 0 12px', color: '#8c8c8c' }}>
          {component.text || ''}
        </p>
      );
    case 'text_input':
    case 'text_email':
    case 'text_phone':
      return (
        <div key={key} style={{ marginBottom: 14 }}>
          <Input
            placeholder={component.placeholder || 'Short answer'}
            disabled
            style={{ borderRadius: 8 }}
          />
        </div>
      );
    case 'text_area':
      return (
        <div key={key} style={{ marginBottom: 14 }}>
          <Text style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#555' }}>
            {component.label || 'Paragraph'}
          </Text>
          <TextArea rows={3} disabled placeholder="Enter text..." style={{ borderRadius: 8 }} />
        </div>
      );
    case 'radio':
      if (component.data_mode === 'dynamic') {
        return (
          <div key={key} style={{ marginBottom: 14 }}>
            <Text style={{ display: 'block', marginBottom: 8, fontSize: 13, color: '#555' }}>
              {component.label || 'Choose one'}
            </Text>
            {(component.example_options || []).length > 0 ? (
              <Radio.Group disabled style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {component.example_options.map((opt) => (
                  <Radio key={opt.id} value={opt.id}>
                    {opt.title || opt.id}
                  </Radio>
                ))}
              </Radio.Group>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Options load from API ({component.data_source_key || 'options'})
              </Text>
            )}
          </div>
        );
      }
      return (
        <div key={key} style={{ marginBottom: 14 }}>
          <Text style={{ display: 'block', marginBottom: 8, fontSize: 13, color: '#555' }}>
            {component.label || 'Choose one'}
          </Text>
          <Radio.Group disabled style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(component.options || []).map((opt) => (
              <Radio key={opt.value} value={opt.value}>
                {opt.label || opt.value}
              </Radio>
            ))}
          </Radio.Group>
        </div>
      );
    case 'navigation_list': {
      const items =
        component.data_mode === 'dynamic'
          ? component.example_items || []
          : component.items || [];

      return (
        <div key={key} style={{ marginBottom: 14 }}>
          <Text style={{ display: 'block', marginBottom: 8, fontSize: 13, color: '#555' }}>
            {component.label || 'Select an option'}
          </Text>
          {items.length > 0 ? (
            items.map((item) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  gap: 12,
                  padding: '10px 0',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                {item.image_src ? (
                  <img
                    src={item.image_src}
                    alt={item.image_alt || item.title || 'Item'}
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 8,
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: 8,
                      background: '#f0f0f0',
                      flexShrink: 0,
                    }}
                  />
                )}
                <div style={{ minWidth: 0 }}>
                  <Text strong style={{ display: 'block', fontSize: 14 }}>
                    {item.title || item.id}
                  </Text>
                  {item.description ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {item.description}
                    </Text>
                  ) : null}
                </div>
              </div>
            ))
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {component.data_mode === 'dynamic'
                ? `Items load from API (${component.data_source_key || 'items'})`
                : 'Add list items in the editor'}
            </Text>
          )}
        </div>
      );
    }
    case 'checkbox':
      if (component.data_mode === 'dynamic') {
        return (
          <div key={key} style={{ marginBottom: 14 }}>
            <Text style={{ display: 'block', marginBottom: 8, fontSize: 13, color: '#555' }}>
              {component.label || 'Select options'}
            </Text>
            {(component.example_options || []).length > 0 ? (
              <Checkbox.Group
                disabled
                options={component.example_options.map((opt) => ({
                  label: opt.title || opt.id,
                  value: opt.id,
                }))}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              />
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Options load from API ({component.data_source_key || 'options'})
              </Text>
            )}
          </div>
        );
      }
      return (
        <div key={key} style={{ marginBottom: 14 }}>
          <Text style={{ display: 'block', marginBottom: 8, fontSize: 13, color: '#555' }}>
            {component.label || 'Select options'}
          </Text>
          <Checkbox.Group
            disabled
            options={(component.options || []).map((opt) => ({
              label: opt.label || opt.value,
              value: opt.value,
            }))}
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          />
        </div>
      );
    case 'dropdown':
      if (component.data_mode === 'dynamic') {
        return (
          <div key={key} style={{ marginBottom: 14 }}>
            <Text style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#555' }}>
              {component.label || 'Select option'}
            </Text>
            {(component.example_options || []).length > 0 ? (
              <Select
                disabled
                placeholder="Select..."
                style={{ width: '100%' }}
                options={component.example_options.map((opt) => ({
                  label: opt.title || opt.id,
                  value: opt.id,
                }))}
              />
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Options load from API ({component.data_source_key || 'options'})
              </Text>
            )}
          </div>
        );
      }
      return (
        <div key={key} style={{ marginBottom: 14 }}>
          <Text style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#555' }}>
            {component.label || 'Select option'}
          </Text>
          <Select
            disabled
            placeholder="Select..."
            style={{ width: '100%' }}
            options={(component.options || []).map((opt) => ({
              label: opt.label || opt.value,
              value: opt.value,
            }))}
          />
        </div>
      );
    case 'date_picker':
      return (
        <div key={key} style={{ marginBottom: 14 }}>
          <Text style={{ display: 'block', marginBottom: 6, fontSize: 13, color: '#555' }}>
            {component.label || 'Select date'}
          </Text>
          <DatePicker disabled style={{ width: '100%' }} />
        </div>
      );
    case 'opt_in':
      return (
        <div key={key} style={{ marginBottom: 14 }}>
          <Checkbox disabled>{component.label || 'I agree'}</Checkbox>
        </div>
      );
    case 'image':
      return (
        <div
          key={key}
          style={{
            marginBottom: 14,
            borderRadius: 8,
            background: '#f0f0f0',
            minHeight: 120,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {component.src ? (
            <img
              src={component.src}
              alt={component.alt || 'Image'}
              style={{ width: '100%', maxHeight: 180, objectFit: 'cover' }}
            />
          ) : (
            <Text type="secondary">Image</Text>
          )}
        </div>
      );
    default:
      return null;
  }
};

const MetaFlowDrawerContent = ({
  screen,
  screens,
  onClose,
  onNext,
  onBack,
}) => {
  if (!screen) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Text type="secondary">Add screens and content to preview your flow.</Text>
      </div>
    );
  }

  const contentComponents = (screen.components || []).filter((c) => c.type !== 'button');
  const footerButton = (screen.components || []).find((c) => c.type === 'button');
  const screenIndex = screens.findIndex((s) => s.id === screen.id);
  const isLastScreen = screenIndex >= screens.length - 1;
  const canGoBack = screenIndex > 0;
  const buttonLabel = footerButton?.label || (isLastScreen ? 'Complete' : 'Continue');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid #f0f0f0',
          background: '#fff',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
          {canGoBack ? (
            <Button
              type="text"
              size="small"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              style={{ flexShrink: 0 }}
            />
          ) : null}
          <Text
            strong
            style={{
              fontSize: 15,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {screen.title || 'Screen'}
          </Text>
        </div>
        <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px' }}>
        {contentComponents.length > 0 ? (
          contentComponents.map((component) => renderComponent(component))
        ) : (
          <Text type="secondary">No content on this screen yet.</Text>
        )}
      </div>

      <div
        style={{
          padding: '12px 16px 16px',
          borderTop: '1px solid #f0f0f0',
          background: '#fff',
        }}
      >
        <Button
          type="primary"
          block
          size="large"
          onClick={onNext}
          style={{
            background: '#1faa52',
            borderColor: '#1faa52',
            borderRadius: 24,
            height: 44,
            fontWeight: 600,
          }}
        >
          {buttonLabel}
        </Button>
        {screens.length > 1 ? (
          <Text
            type="secondary"
            style={{ display: 'block', textAlign: 'center', marginTop: 8, fontSize: 11 }}
          >
            Screen {screenIndex + 1} of {screens.length}
          </Text>
        ) : null}
      </div>
    </div>
  );
};

export default MetaFlowDrawerContent;
